import { ColScaffold, RowScaffold } from "../../interface";

/**
 * Builds the set of cell entries for a rectangular range between two cells.
 * Includes all rows and columns whose numbers fall between the anchor and target (inclusive).
 *
 * @param rows - All available scaffold rows.
 * @param cols - All available scaffold columns.
 * @param anchorRowId - The row ID of the selection anchor.
 * @param anchorColId - The column ID of the selection anchor.
 * @param targetRowId - The row ID of the selection target.
 * @param targetColId - The column ID of the selection target.
 * @returns An array of cell entries covering the rectangular range.
 */
export const buildCellRange = <T>(
  rows: RowScaffold<T>[],
  cols: ColScaffold<T>[],
  anchorRowId: string,
  anchorColId: string,
  targetRowId: string,
  targetColId: string,
): { rowId: string; colId: string[] }[] => {
  const anchorRow = rows.find((r) => r.id === anchorRowId);
  const targetRow = rows.find((r) => r.id === targetRowId);
  const anchorCol = cols.find((c) => c.id === anchorColId);
  const targetCol = cols.find((c) => c.id === targetColId);

  if (!anchorRow || !targetRow || !anchorCol || !targetCol) {
    return [];
  }

  const minRowNum = Math.min(anchorRow.rowNumber, targetRow.rowNumber);
  const maxRowNum = Math.max(anchorRow.rowNumber, targetRow.rowNumber);
  const minColNum = Math.min(anchorCol.colNumber, targetCol.colNumber);
  const maxColNum = Math.max(anchorCol.colNumber, targetCol.colNumber);

  const rangeRows = rows.filter((r) => r.rowNumber >= minRowNum && r.rowNumber <= maxRowNum);
  const rangeCols = cols.filter((c) => c.colNumber >= minColNum && c.colNumber <= maxColNum);

  return rangeRows.map((r) => ({ rowId: r.id, colId: rangeCols.map((c) => c.id) }));
};
